import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { fetchStatistics, type WeeklyBreakdown } from '@/services/statistics';

function buildChartData(weeklyBreakdown: WeeklyBreakdown[]) {
  return weeklyBreakdown.map((day) => ({
    day: day.day,
    'Morning On Time': day.morningOnTime,
    'Morning Late': day.morningLate,
    'Morning Missed': day.morningMissed,
    'Bedtime On Time': day.bedtimeOnTime,
    'Bedtime Late': day.bedtimeLate,
    'Bedtime Missed': day.bedtimeMissed,
    Skipped: day.skipped,
  }));
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, p) => sum + p.value, 0);
  return (
    <div className="bg-background-100 border border-background-200/70 rounded-md px-3 py-2 shadow-sm">
      <p className="text-xs font-semibold text-foreground-900 mb-1">{label}</p>
      {payload.map((entry, idx) => (
        entry.value > 0 && (
          <div key={idx} className="flex items-center gap-2 text-[11px]">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-foreground-600">{entry.name}:</span>
            <span className="text-foreground-900 font-medium">{entry.value}</span>
          </div>
        )
      ))}
      <div className="border-t border-background-200/60 mt-1 pt-1 text-[11px] text-foreground-500">
        Total: {total}
      </div>
    </div>
  );
}

export default function WeeklyChartWidget() {
  const [chartData, setChartData] = useState<ReturnType<typeof buildChartData> | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchStatistics().then((stats) => {
      if (mounted && stats) setChartData(buildChartData(stats.weeklyBreakdown));
    });
    return () => { mounted = false; };
  }, []);

  if (!chartData) {
    return (
      <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden animate-pulse">
        <div className="px-4 py-3 border-b border-background-200/60">
          <div className="h-5 w-40 rounded bg-background-200" />
        </div>
        <div className="p-4"><div className="h-[280px] rounded bg-background-200/50" /></div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-primary-500 text-lg" />
          </span>
          <h3 className="text-sm font-heading font-semibold text-foreground-950">Weekly Breakdown</h3>
        </div>
        <span className="text-[10px] text-foreground-400 font-mono bg-background-200 px-2 py-0.5 rounded-full">
          This week
        </span>
      </div>

      <div className="p-4">
        {/* Morning section */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sun-line text-amber-500 text-xs" />
            </span>
            <span className="text-xs font-medium text-foreground-600">Morning</span>
          </div>
          <div className="h-[140px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200) / 0.6)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} width={24} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Morning On Time" stackId="morning" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Morning Late" stackId="morning" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Morning Missed" stackId="morning" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bedtime section */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-moon-line text-indigo-400 text-xs" />
            </span>
            <span className="text-xs font-medium text-foreground-600">Bedtime</span>
          </div>
          <div className="h-[140px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200) / 0.6)" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: 'oklch(var(--foreground-400))' }} axisLine={false} tickLine={false} width={24} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="Bedtime On Time" stackId="bedtime" fill="#818cf8" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Bedtime Late" stackId="bedtime" fill="#f59e0b" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Bedtime Missed" stackId="bedtime" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap mt-3 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
            <span className="text-[10px] text-foreground-500">On Time</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
            <span className="text-[10px] text-foreground-500">Late</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-rose-500" />
            <span className="text-[10px] text-foreground-500">Missed</span>
          </div>
        </div>
      </div>
    </div>
  );
}